import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import {
  AverageLastScoreLocation,
  Position,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import {
  MAX_SCORE as FOOTBALL_MAX_SCORE,
  playerScoreThresholds as footballThresholds,
} from '@sorare/core/src/lib/footballThresholds';
import { inventoryMessages } from '@sorare/core/src/lib/glossary';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';
import {
  MLB_MAX_SCORE,
  NBA_MAX_SCORE,
  mlbThresholds,
  nbaThresholds,
} from '@sorare/core/src/lib/usSportsThresholds';
import { Color } from '@sorare/core/src/style/types';

export enum FilterKey {
  COMPETITION = 'competition',
  TEAM = 'team',
  HOME_AWAY = 'homeAway',
  MODE = 'mode',
  POSITION = 'position',
}

export const ALL_VALUE = 'ALL' as const;
export const ALL_TEAMS_ITEM: {
  value: string;
  label: ReactNode;
} = {
  value: ALL_VALUE,
  label: (
    <LabelM bold>
      <FormattedMessage {...inventoryMessages.allTeams} />
    </LabelM>
  ),
};

export const ALL_COMPETITIONS_ITEM: {
  value: string;
  label: ReactNode;
} = {
  value: ALL_VALUE,
  label: (
    <LabelM bold>
      <FormattedMessage
        id="fixtureChart.competitionFilter.all"
        defaultMessage="Competitions"
      />
    </LabelM>
  ),
};

const homeAwayValues = [
  ALL_VALUE,
  AverageLastScoreLocation.HOME,
  AverageLastScoreLocation.AWAY,
] as const;

const homeAwayLabels = defineMessages({
  [AverageLastScoreLocation.HOME]: {
    id: 'Score.home',
    defaultMessage: 'Home',
  },
  [AverageLastScoreLocation.AWAY]: {
    id: 'Score.away',
    defaultMessage: 'Away',
  },
  [ALL_VALUE]: {
    id: 'Score.all',
    defaultMessage: 'All',
  },
});

export const homeAwayOptions = homeAwayValues.map(val => ({
  value: val,
  label: (
    <LabelM bold>
      <FormattedMessage {...homeAwayLabels[val]} />
    </LabelM>
  ),
}));

type WithAllValue<T> = T | typeof ALL_VALUE;

export type Filters = {
  [FilterKey.COMPETITION]: WithAllValue<string>;
  [FilterKey.TEAM]: WithAllValue<string>;
  [FilterKey.HOME_AWAY]: WithAllValue<AverageLastScoreLocation>;
  [FilterKey.MODE]: TotalAveragePlayerScore;
  [FilterKey.POSITION]: Position;
};

export type Options = {
  [FilterKey.COMPETITION]: {
    value: string;
    label: ReactNode;
  }[];
  [FilterKey.TEAM]: {
    value: string;
    label: ReactNode;
  }[];
  [FilterKey.HOME_AWAY]: {
    value: WithAllValue<AverageLastScoreLocation>;
    label: ReactNode;
  }[];
  [FilterKey.POSITION]: {
    value: Position;
    label: ReactNode;
  }[];
  [FilterKey.MODE]: {
    value: TotalAveragePlayerScore;
    label: ReactNode;
  }[];
};

const thresholds = {
  [Sport.FOOTBALL]: footballThresholds,
  [Sport.NBA]: nbaThresholds,
  [Sport.BASEBALL]: mlbThresholds,
};
const maxScores = {
  [Sport.FOOTBALL]: FOOTBALL_MAX_SCORE,
  [Sport.NBA]: NBA_MAX_SCORE,
  [Sport.BASEBALL]: MLB_MAX_SCORE,
};

export const normalizeStatsFromRawScores = (
  rawScores: (number | null)[],
  sport: Sport
) => {
  const sportThresholds = thresholds[sport];
  const thresholdsScores = sportThresholds.map(t => t[0]);
  const scaleScores = [...thresholdsScores, maxScores[sport]]
    .map((s, i, arr) => (arr[i + 1] ? [s, s + (arr[i + 1] - s) / 2] : s))
    .flat();

  const scaleSize = scaleScores.length;
  return [...rawScores].reverse().map(so5Score => {
    let color: Color;
    let percent: number;
    if (so5Score === null) {
      percent = 1 / scaleSize;
      color = 'var(--c-nd-600)';
    } else if (so5Score < 0) {
      percent = 1 / scaleSize;
      color = 'var(--c-red-400)';
    } else {
      const thresholdIndex = scaleScores.findIndex(n => n > so5Score);
      percent =
        ((thresholdIndex >= 0 ? thresholdIndex : scaleScores.length) + 1) /
        scaleSize;
      color = findThresholdColor(so5Score, sport);
    }

    return { percent, color };
  });
};
